import apiClient from "./api";

export interface ToolProperty {
  type: string;
  description?: string;
  enum?: any;
  items?: ToolProperty;
  properties?: { [key: string]: ToolProperty };
  required?: string[];
}

export interface ToolInputSchema {
  type: string;
  properties: { [key: string]: ToolProperty };
  required?: string[];
}

export interface ToolConfig {
  id: string;
  serverId: string;
  name: string;
  description: string;
  httpMethod: string;
  endpointPath: string;
  inputSchemaJson: string;
  outputSchemaJson?: string;
  requestBodyTemplate?: string;
  isActive: boolean;
  displayName: string;
  riskLevel: "low" | "medium" | "high";
  isDestructive: boolean;
  isBulk: boolean;
  requiresApproval: boolean;
}

export interface UpdateToolRequest {
  name?: string;
  httpMethod?: string;
  endpointPath?: string;
  description?: string;
  inputSchemaJson?: string;
  displayName?: string;
  riskLevel?: "low" | "medium" | "high";
  isDestructive?: boolean;
  isBulk?: boolean;
  requiresApproval?: boolean;
}

export interface CreateToolRequest {
  name: string;
  description?: string;
  httpMethod: string;
  endpointPath: string;
  inputSchemaJson?: string;
  outputSchemaJson?: string;
  requestBodyTemplate?: string;
  isActive?: boolean;
  displayName: string;
  riskLevel: "low" | "medium" | "high";
  isDestructive: boolean;
  isBulk: boolean;
  requiresApproval: boolean;
}

export const toolService = {
  getServerTools: async (serverId: string): Promise<ToolConfig[]> => {
    const response = await apiClient.get(`/servers/${serverId}/tools`);
    return response.data;
  },

  getServerToolsPaged: async (
    serverId: string,
    page: number = 1,
    pageSize: number = 50,
    searchQuery?: string,
    isActive?: boolean,
    riskLevel?: string,
  ): Promise<{
    items: ToolConfig[];
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  }> => {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    params.append("pageSize", pageSize.toString());
    if (searchQuery) params.append("searchQuery", searchQuery);
    if (isActive !== undefined) params.append("isActive", isActive.toString());
    if (riskLevel) params.append("riskLevel", riskLevel);

    const response = await apiClient.get(
      `/servers/${serverId}/tools/paged?${params.toString()}`,
    );
    return response.data;
  },

  createTool: async (
    serverId: string,
    data: CreateToolRequest,
  ): Promise<ToolConfig> => {
    const response = await apiClient.post(`/servers/${serverId}/tools`, data);
    return response.data;
  },

  updateTool: async (
    serverId: string,
    toolId: string,
    data: UpdateToolRequest,
  ): Promise<ToolConfig> => {
    const response = await apiClient.put(
      `/servers/${serverId}/tools/${toolId}`,
      data,
    );
    return response.data;
  },

  toggleToolStatus: async (
    serverId: string,
    toolId: string,
    isActive: boolean,
  ): Promise<ToolConfig> => {
    const response = await apiClient.patch(
      `/servers/${serverId}/tools/${toolId}/status`,
      { isActive },
    );
    return response.data;
  },

  deleteTool: async (serverId: string, toolId: string): Promise<void> => {
    await apiClient.delete(`/servers/${serverId}/tools/${toolId}`);
  },

  bulkEnableByIds: async (
    serverId: string,
    toolIds: string[],
  ): Promise<{ enabledCount: number; failedIds: string[] }> => {
    const response = await apiClient.patch(
      `/servers/${serverId}/tools/bulk-enable`,
      { toolIds },
    );
    return response.data;
  },

  bulkDisableByIds: async (
    serverId: string,
    toolIds: string[],
  ): Promise<{ enabledCount: number; failedIds: string[] }> => {
    const response = await apiClient.patch(
      `/servers/${serverId}/tools/bulk-disable`,
      { toolIds },
    );
    return response.data;
  },

  bulkEnableByRisk: async (
    serverId: string,
    riskLevel?: "low" | "medium" | "high",
  ): Promise<{ enabledCount: number; failedIds: string[] }> => {
    const response = await apiClient.patch(
      `/servers/${serverId}/tools/bulk-enable-by-risk`,
      { riskLevel },
    );
    return response.data;
  },

  regenerateTool: async (
    serverId: string,
    toolId: string,
  ): Promise<ToolConfig> => {
    const response = await apiClient.post(
      `/servers/${serverId}/tools/${toolId}/regenerate`,
    );
    return response.data;
  },

  parseInputSchema: (inputSchemaJson: string): ToolInputSchema | null => {
    try {
      return JSON.parse(inputSchemaJson);
    } catch {
      return null;
    }
  },

  stringifyInputSchema: (schema: ToolInputSchema): string => {
    return JSON.stringify(schema);
  },
};
